import { useElapsedTime } from "hooks/useElapsedTime";

/** A stage becomes active once `afterMs` has elapsed. Extend with any payload. */
export type TimedStage = { afterMs: number };

type UseTimedStageOptions<T extends TimedStage> = {
  /** Run the timer while true; no stage is active when false. */
  active: boolean;
  /** Stages to choose from. Order doesn't matter — the latest eligible wins. */
  stages: readonly T[];
  /** Epoch ms to measure from. Defaults to when the timer became active. */
  startAt?: number;
  /** How often to re-evaluate the active stage. */
  intervalMs?: number;
};

/**
 * Picks the active stage from a list based on how long a process has been
 * running: each stage activates once its `afterMs` of elapsed time has passed,
 * and the one with the largest elapsed threshold wins. Returns null before the
 * first stage (or while inactive).
 *
 * Generic over the stage shape, so any loading UI plugs in its own copy/times
 * and renders whatever fields it attached:
 *
 *   const stage = useTimedStage({
 *     active: isLoading,
 *     stages: [
 *       { afterMs: 10_000, message: "Filling form…" },
 *       { afterMs: 14_000, message: "Saving PDF…" },
 *     ],
 *   });
 *   // stage?.message
 */
export function useTimedStage<T extends TimedStage>({
  active,
  stages,
  startAt,
  intervalMs,
}: UseTimedStageOptions<T>): T | null {
  const elapsedMs = useElapsedTime({ active, startAt, intervalMs });

  return stages.reduce<T | null>((winner, stage) => {
    if (elapsedMs < stage.afterMs) return winner;
    return !winner || stage.afterMs > winner.afterMs ? stage : winner;
  }, null);
}
